
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCategorias } from '@/hooks/useCategorias';
import { CategoriaFormData } from '@/types/categoria';
import CategoriaFormFields from './CategoriaFormFields';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';

const categoriaSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
  descricao: z.string().optional(),
  tipo: z.enum(['incidente', 'solicitacao', 'problema', 'requisicao', 'mudanca', 'plano_acao']),
  categoria_pai_id: z.string().optional(),
  ordem_exibicao: z.number().min(0, 'Ordem deve ser maior ou igual a 0'),
  ativo: z.boolean(),
  client_id: z.string().optional(),
  grupo_id: z.string().optional(),
  sla_id: z.string().optional(),
  usuario_responsavel_id: z.string().optional(),
});

interface NewCategoriaDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewCategoriaDialog = ({ isOpen, onClose }: NewCategoriaDialogProps) => {
  const { createCategoria } = useCategorias();
  const [activeTab, setActiveTab] = useState('info');

  const form = useForm<CategoriaFormData>({
    resolver: zodResolver(categoriaSchema),
    defaultValues: {
      nome: '',
      descricao: '',
      tipo: 'incidente',
      ordem_exibicao: 0,
      ativo: true,
      categoria_pai_id: 'none',
      client_id: 'none',
      grupo_id: 'none',
      sla_id: 'none',
      usuario_responsavel_id: 'none',
    },
  });

  const onSubmit = async (data: CategoriaFormData) => {
    try {
      // Convert "none" values to undefined for the API
      const cleanData: Partial<CategoriaFormData> = {};
      Object.entries(data).forEach(([key, value]) => {
        if (value !== 'none' && value !== '' && value !== null) {
          (cleanData as any)[key] = value;
        }
      });

      await createCategoria.mutateAsync(cleanData as CategoriaFormData);
      form.reset();
      onClose();
    } catch (error) {
      console.error('Erro ao criar categoria:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Categoria</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="info">Informações Gerais</TabsTrigger>
            <TabsTrigger value="campos" disabled>Campos Customizados</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4">
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Primeiro crie a categoria, depois você poderá configurar campos customizados na edição.
              </AlertDescription>
            </Alert>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <CategoriaFormFields form={form} />

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={createCategoria.isPending}>
                    {createCategoria.isPending ? 'Criando...' : 'Criar Categoria'}
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="campos" className="space-y-4">
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Esta aba estará disponível após criar a categoria.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
